import React, { useEffect, useState } from "react";
import { buildDetailPath } from "@shared/navigation/tabs/detailRoutes";
import { viewHref } from "@shared/navigation/tabs/ViewsProvider";
import { useNavigate } from "react-router-dom";
import GenericCard from "@ui-components/Card/GenericCard";
import {
  GenericTable,
  GenericTableColumn,
} from "@ui-components/Table/GenericTable";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import { fetchExtenderPopPortsAPI } from "./requests/request";

interface AssociatedPort {
  id: number;
  portId: string;
  name: string | null;
  capacityMbps: number | null;
}

interface ExtenderPopPortsProps {
  extenderPopId: number;
  tenantId: number;
}

/**
 * "Associated Ports" of an Extended PoP: the customer's KE Ports delivered
 * through its router. The services hang off each port and live on the
 * port's own detail — this table is the bridge to them, with the same
 * link treatment the Ports list gives the Port ID.
 */
export default function ExtenderPopPorts({
  extenderPopId,
  tenantId,
}: ExtenderPopPortsProps) {
  const navigate = useNavigate();
  const api = useAxiosAuth0();
  const [ports, setPorts] = useState<AssociatedPort[]>([]);
  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        setIsFetching(true);
        const response = await fetchExtenderPopPortsAPI(
          api,
          extenderPopId,
          tenantId
        );
        if (cancelled) return;
        setPorts(response.data?.data?.ports ?? []);
      } catch (error) {
        console.error("Error fetching Extended PoP ports", error);
      } finally {
        if (!cancelled) setIsFetching(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [api, extenderPopId, tenantId]);

  const columns: GenericTableColumn<AssociatedPort>[] = [
    {
      header: "Port ID",
      accessor: "portId",
      renderCell: (row) => (
        <button
          {...viewHref(buildDetailPath("port", { portId: row.id }))}
          onClick={() => navigate(buildDetailPath("port", { portId: row.id }))}
          className="hover:text-semantic-text-brand hover:underline focus:underline active:underline font-medium underline text-semantic-text-brand"
        >
          {row.portId}
        </button>
      ),
    },
    {
      header: "Name",
      accessor: "name",
      renderCell: (row) => <div>{row.name ?? "-"}</div>,
    },
    {
      header: "Capacity",
      sortAccessor: (row) => row.capacityMbps ?? 0,
      renderCell: (row) => (
        <div>
          {row.capacityMbps != null ? `${row.capacityMbps / 1000} Gbps` : "-"}
        </div>
      ),
    },
  ];

  return (
    <GenericCard
      isLoading={isFetching}
      headerContent="Associated Ports"
      isCollapsible={false}
      bodyContent={
        <GenericTable
          columns={columns}
          data={ports}
          initialRowsPerPage={5}
          rowsPerPageOptions={[5, 10, 20]}
          width="100%"
          tableClassName="bg-semantic-bg-primary"
        />
      }
    />
  );
}
